import { Children, Fragment, isValidElement, ReactNode, RefObject } from 'react';
import {
  Breadcrumb as ChakraBreadcrumbNamespace,
  BreadcrumbItemProps as ChakraBreadcrumbItemProps,
  BreadcrumbLinkProps as ChakraBreadcrumbLinkProps,
  HStack,
  Text,
} from '@chakra-ui/react';

import { MdChevronRight, OnOutlineHome } from '../../../icons';

export type BreadcrumbRootProps = ChakraBreadcrumbNamespace.RootProps;

export function BreadcrumbRoot(
  props: Omit<BreadcrumbRootProps, 'children'> & { children?: ReactNode | Iterable<ReactNode> }
) {
  const { children, ...rest } = props;
  const validChildren = Children.toArray(children).filter(isValidElement);
  return (
    <ChakraBreadcrumbNamespace.Root {...rest}>
      <ChakraBreadcrumbNamespace.List>
        {validChildren.map((child, index) => {
          const last = index === validChildren.length - 1;
          return (
            <Fragment key={index}>
              {child}
              {!last && (
                <ChakraBreadcrumbNamespace.Separator>
                  <MdChevronRight />
                </ChakraBreadcrumbNamespace.Separator>
              )}
            </Fragment>
          );
        })}
      </ChakraBreadcrumbNamespace.List>
    </ChakraBreadcrumbNamespace.Root>
  );
}

export type BreadcrumbItemProps = Omit<ChakraBreadcrumbItemProps, 'children'> & {
  children: ReactNode | Iterable<ReactNode>;
  isHomePage?: boolean;
  shouldWrap?: boolean;
  ref?: RefObject<HTMLDivElement | null>;
};
export function BreadcrumbItem(props: BreadcrumbItemProps) {
  const { children, shouldWrap, ...rest } = props;

  return (
    <ChakraBreadcrumbNamespace.Item whiteSpace={shouldWrap === false ? 'nowrap' : undefined} {...rest}>
      {children}
    </ChakraBreadcrumbNamespace.Item>
  );
}

export interface BreadcrumbLinkProps extends BreadcrumbItemProps {
  linkProps: ChakraBreadcrumbLinkProps;
}
export function BreadcrumbLink(props: BreadcrumbLinkProps) {
  const { children, linkProps, ...rest } = props;

  return (
    <BreadcrumbItem {...rest}>
      <ChakraBreadcrumbNamespace.Link {...linkProps}>{children}</ChakraBreadcrumbNamespace.Link>
    </BreadcrumbItem>
  );
}

export function BreadcrumbCurrentLink(props: BreadcrumbItemProps) {
  const { children, isHomePage, ...rest } = props;

  return (
    <BreadcrumbItem {...rest}>
      <ChakraBreadcrumbNamespace.CurrentLink>
        {props.isHomePage ? (
          <HStack gap={1}>
            <OnOutlineHome boxSize={4} viewBox="0 0 16 16" />
            <Text>{children}</Text>
          </HStack>
        ) : (
          children
        )}
      </ChakraBreadcrumbNamespace.CurrentLink>
    </BreadcrumbItem>
  );
}

export function BreadcrumbEllipsis(props: BreadcrumbItemProps) {
  const { children, ...rest } = props;

  return (
    <BreadcrumbItem {...rest}>
      <ChakraBreadcrumbNamespace.Ellipsis>{children}</ChakraBreadcrumbNamespace.Ellipsis>
    </BreadcrumbItem>
  );
}
